import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Briefcase, Layers, CreditCard, Cpu } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { IMAGES, CREDIT_URL } from "@/data/site";
import { StarBleed } from "./CosmicField";

const PortfolioTag = () => (
  <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-white/35">
    <span className="h-1 w-1 rounded-full bg-cyan-300/70" />
    Part of the ArcaCentum portfolio
  </span>
);

const DevBadge = () => (
  <span
    data-testid="product-dev-badge"
    className="inline-flex w-fit items-center gap-1.5 rounded-full border border-amber-300/25 bg-amber-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-amber-200"
  >
    <span className="h-1 w-1 rounded-full bg-amber-300" />
    In Development · Coming Soon
  </span>
);

export const Products = () => (
  <section id="products" data-testid="products-section" className="relative py-28 sm:py-40">
    <StarBleed count={10} height="h-[340px]" opacity={0.45} />
    <div className="pointer-events-none absolute top-40 -left-20 h-[380px] w-[380px] glow-violet opacity-50" />

    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-16">
        <div>
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-violet-300/80">
              02 — Our Products
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-500 tracking-[-0.02em] text-white">
              A growing portfolio, led by a flagship.
            </h2>
          </Reveal>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Flagship — ArcaCentum.ai */}
        <Reveal delay={0.05}>
          <div
            data-testid="product-flagship"
            className="glass glass-hover group relative h-full overflow-hidden rounded-3xl flex flex-col"
          >
            <div className="relative h-52 overflow-hidden border-b border-white/[0.06] bg-black">
              <img
                src={IMAGES.productSpotlight}
                alt="ArcaCentum.ai — AI-native app studio preview"
                className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/20 to-transparent" />
              <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full border border-violet-400/30 bg-violet-500/15 px-3 py-1 text-[11px] uppercase tracking-widest text-violet-100 backdrop-blur-sm">
                <Sparkles size={12} /> Flagship Product
              </span>
            </div>

            <div className="flex flex-1 flex-col p-8 sm:p-9">
              <h3 className="font-display text-3xl font-600 text-white tracking-tight">
                ArcaCentum<span className="text-gradient">.ai</span>
              </h3>
              <p className="mt-4 text-base leading-relaxed text-white/60 font-light">
                An AI-native app studio that helps users turn ideas into real iOS
                and Android applications using artificial intelligence.
              </p>
              <div className="mt-auto pt-8 flex flex-col gap-4">
                <DevBadge />
                <PortfolioTag />
                <a
                  href="https://arcacentum.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-visit-ai-btn"
                  className="group/btn inline-flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-500 text-[#0a0a0f] transition-all duration-300 hover:gap-3"
                >
                  Learn More
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Second product — ArcaCapitalis */}
        <Reveal delay={0.12}>
          <div
            data-testid="product-capitalis"
            className="glass glass-hover group relative h-full overflow-hidden rounded-3xl flex flex-col"
          >
            <div className="relative h-52 overflow-hidden border-b border-white/[0.06] bg-[#0c0c14]">
              <img
                src={IMAGES.capitalisVisual}
                alt="ArcaCapitalis — strategic business building and consulting"
                className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/20 to-transparent" />
              <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full border border-cyan-300/25 bg-cyan-400/10 px-3 py-1 text-[11px] uppercase tracking-widest text-cyan-100 backdrop-blur-sm">
                <Layers size={12} /> Second Product
              </span>
            </div>

            <div className="flex flex-1 flex-col p-8 sm:p-9">
              <h3 className="font-display text-3xl font-600 text-white tracking-tight">
                Arca<span className="text-gradient">Capitalis</span>
              </h3>
              <p className="mt-4 text-base leading-relaxed text-white/60 font-light">
                Strategic business building and consulting for entrepreneurs and
                growing companies. Helps founders develop ideas, conduct research,
                structure operations, and build strong foundations.
              </p>
              <div className="mt-auto pt-8 flex flex-col gap-4">
                <DevBadge />
                <PortfolioTag />
                <a
                  href="https://arcacapitalis.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-visit-capitalis-btn"
                  className="group/btn inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3 text-sm font-500 text-white transition-all duration-300 hover:gap-3 hover:border-white/35 hover:bg-white/[0.06]"
                >
                  Learn More
                  <ArrowUpRight size={16} className="text-cyan-200 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Third product — ArcaCentum Credit */}
        <Reveal delay={0.18}>
          <div
            data-testid="product-credit"
            className="glass glass-hover group relative h-full overflow-hidden rounded-3xl flex flex-col"
          >
            <div className="relative h-52 overflow-hidden border-b border-white/[0.06] bg-[#0b0b14]">
              <img
                src={IMAGES.creditVisual}
                alt="ArcaCentum Credit — AI-powered business and personal credit platform"
                className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/20 to-transparent" />
              <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full border border-blue-400/30 bg-blue-500/10 px-3 py-1 text-[11px] uppercase tracking-widest text-blue-100 backdrop-blur-sm">
                <CreditCard size={12} /> Third Product
              </span>
            </div>

            <div className="flex flex-1 flex-col p-8 sm:p-9">
              <h3 className="font-display text-3xl font-600 text-white tracking-tight">
                ArcaCentum <span className="text-gradient">Credit</span>
              </h3>
              <p className="mt-4 text-base leading-relaxed text-white/60 font-light sm:hidden">
                AI-powered business and personal credit building, credit report analysis,
                dispute assistance, Net-30 education, and funding-readiness tools.
              </p>
              <p className="mt-4 text-base leading-relaxed text-white/60 font-light hidden sm:block">
                An AI-powered business and personal credit platform that helps entrepreneurs
                and individuals understand, organize, build, and manage credit through guided
                roadmaps, credit report analysis, dispute assistance, Net-30 education, vendor
                tracking, document management, and funding readiness tools.
              </p>
              <div className="mt-auto pt-8 flex flex-col gap-4">
                <DevBadge />
                <PortfolioTag />
                <a
                  href={CREDIT_URL}
                  data-testid="product-visit-credit-btn"
                  className="group/btn inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3 text-sm font-500 text-white transition-all duration-300 hover:gap-3 hover:border-white/35 hover:bg-white/[0.06]"
                >
                  Learn More
                  <ArrowUpRight size={16} className="text-blue-200 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Fourth product — ArcaOS */}
        <Reveal delay={0.24}>
          <div
            data-testid="product-arcaos"
            className="glass glass-hover group relative h-full overflow-hidden rounded-3xl flex flex-col"
          >
            <div className="relative h-52 overflow-hidden border-b border-white/[0.06] bg-[#0b0b12]">
              <img
                src={IMAGES.arcaosVisual}
                alt="ArcaOS — AI operating system and mission control for the ArcaCentum ecosystem"
                className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/20 to-transparent" />
              <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full border border-violet-400/25 bg-violet-500/10 px-3 py-1 text-[11px] uppercase tracking-widest text-violet-100 backdrop-blur-sm">
                <Cpu size={12} /> AI Operating System &amp; Mission Control
              </span>
            </div>

            <div className="flex flex-1 flex-col p-8 sm:p-9">
              <h3 className="font-display text-3xl font-600 text-white tracking-tight">
                Arca<span className="text-gradient">OS</span>
              </h3>
              <p className="mt-4 text-base leading-relaxed text-white/60 font-light">
                The intelligence and operating layer for the ArcaCentum ecosystem —
                unifying projects, decisions, product health, AI attention, knowledge,
                roadmaps, and executive operations in one command center.
              </p>
              <div className="mt-auto pt-8 flex flex-col gap-4">
                <span
                  data-testid="product-arcaos-badge"
                  className="inline-flex w-fit items-center gap-1.5 rounded-full border border-amber-300/25 bg-amber-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-amber-200"
                >
                  <span className="h-1 w-1 rounded-full bg-amber-300" />
                  In Development · Mission Control V1
                </span>
                <PortfolioTag />
                <span
                  data-testid="product-visit-arcaos-btn"
                  className="inline-flex w-fit cursor-default items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3 text-sm font-500 text-white/70"
                >
                  Coming Soon
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Secondary — more products in development */}
      <Reveal delay={0.1}>
        <div
          data-testid="products-more-note"
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-6 py-5"
        >
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="h-1.5 w-1.5 rounded-full bg-violet-300/60"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.25 }}
                />
              ))}
            </div>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/50">
              More products in development
            </span>
          </div>
          <p className="text-sm leading-relaxed text-white/40 font-light">
            ArcaCentum continues expanding its portfolio of intelligent platforms and
            specialized AI products.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
